// Layer 4 (UI): the selected stage's panel (polish.P11, comp variant A):
// the read (bottleneck read on Grinding, operating window elsewhere), the
// stage's in / out / recycle, the circuit's stages at the cursor and the
// sample points the stage takes (C's hosted samples). Render only — every
// row comes from `StagePanel.transform.ts`.
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { SurfacePagePanel } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { splitLineTail, type FigureParts, type StageFact, type StageRead, type StageRow, type StageSamples } from './StagePanel.transform';

const READ_PILL: Record<StageRead['tone'], string> = {
  ok: 'bg-[color-mix(in_srgb,var(--state-online)_12%,transparent)] text-[var(--state-online)]',
  warn: 'bg-[var(--wash-warning)] text-[var(--state-warning)]',
  bad: 'bg-[color-mix(in_srgb,var(--state-blocked)_12%,transparent)] text-[var(--state-blocked)]',
};

const STATE_PILL: Record<'warn' | 'bad' | 'neutral', string> = {
  warn: 'bg-[var(--wash-warning)] text-[var(--state-warning)]',
  bad: 'bg-[color-mix(in_srgb,var(--state-blocked)_12%,transparent)] text-[var(--state-blocked)]',
  neutral: 'bg-[var(--hairline)] text-ink-secondary',
};

const PILL_BASE =
  'inline-flex items-center whitespace-nowrap rounded-full px-2 py-px text-[10px] font-semibold uppercase leading-4 tracking-[0.05em]';

function Figured({ parts }: { parts: FigureParts }) {
  return (
    <>
      {parts.map((part, index) =>
        part.figure ? (
          <strong key={index} className="font-semibold text-ink-primary">
            {part.text}
          </strong>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </>
  );
}

function SubHead({ label, meta }: { label: string; meta?: string }) {
  return (
    <div className="mt-3.5 flex items-center gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">{label}</span>
      {meta ? <span className="ml-auto whitespace-nowrap text-[11px] text-muted">{meta}</span> : null}
    </div>
  );
}

function StageStatus({ row }: { row: StageRow }) {
  if (row.status.kind === 'pill') {
    return <span className={cn(PILL_BASE, STATE_PILL[row.status.tone])}>{row.status.word}</span>;
  }
  if (row.status.kind === 'on-lines') return null;
  if (row.status.kind === 'not-computed') {
    return (
      <span className="whitespace-nowrap rounded-full border border-dashed border-[var(--hairline-strong)] px-2 py-px text-[11px] text-muted">
        not computed
      </span>
    );
  }
  // A quiet stage already says "N in band"; one whose only reading is its quiet outcome says it plainly.
  return row.inBandLine ? null : <span>in band</span>;
}

export function StagePanel({
  stageName,
  rulesLine,
  advisoryHref,
  read,
  facts,
  stages,
  spcClock,
  samples,
  samplesMeta,
}: {
  stageName: string;
  rulesLine: string | null;
  advisoryHref: string;
  read: StageRead | null;
  facts: readonly StageFact[];
  stages: readonly StageRow[] | null;
  spcClock: string;
  samples: StageSamples;
  samplesMeta: string;
}) {
  return (
    <SurfacePagePanel
      as="section"
      title={stageName}
      meta={rulesLine ?? undefined}
      actions={
        <Link
          to={advisoryHref}
          className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-ink-secondary underline decoration-[var(--hairline-strong)] underline-offset-4 hover:text-ink-primary"
        >
          Open in Advisory
          <ArrowUpRight className="size-3" aria-hidden />
        </Link>
      }
      contentClassName="pb-2.5"
    >
      {read ? (
        <div
          data-stage-read={read.kind}
          className="flex items-start gap-2.5 border-b border-[var(--color-divider)] pb-3 text-[13px] leading-relaxed text-ink-secondary"
        >
          <span className={cn(PILL_BASE, 'mt-0.5 shrink-0', READ_PILL[read.tone])}>{read.label}</span>
          <span>{read.text}</span>
        </div>
      ) : null}
      {facts.length ? (
        <dl className="grid grid-cols-[5.75rem_minmax(0,1fr)] text-[13px]">
          {facts.map((fact) => (
            <div key={fact.label} className="contents">
              <dt className="border-b border-[var(--color-divider)] pb-2 pt-2.5 text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">
                {fact.label}
              </dt>
              <dd className="border-b border-[var(--color-divider)] py-2 text-ink-secondary">
                <Figured parts={fact.parts} />
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {stages ? (
        <Commentable name="CircuitsCanvas">
          <div data-stage-stages>
            <SubHead label="Stages" meta={`SPC at ${spcClock}`} />
            <div>
              {stages.map((row, index) => (
                <div
                  key={row.id}
                  data-stage-row={row.id}
                  className="grid grid-cols-[minmax(0,15rem)_minmax(0,1fr)_auto] items-baseline gap-x-3 border-b border-[var(--color-divider)] py-2 text-[13px] last:border-b-0"
                >
                  <span className="flex items-center gap-1.5 font-semibold text-ink-primary">
                    {index > 0 ? <span className="font-normal text-muted" aria-hidden>→</span> : null}
                    {row.label}
                  </span>
                  <span className="min-w-0 space-y-0.5 tabular-nums text-ink-secondary">
                    {row.lines.length
                      ? row.lines.map((line) => (
                          // The off word flows inline after the line it marks,
                          // held to the line's last word, so a narrow row wraps
                          // the line as a unit instead of dropping the pill onto
                          // a line of its own.
                          <span key={line.id} className="block">
                            {line.pill ? (
                              <>
                                <Figured parts={splitLineTail(line.parts).lead} />
                                <span className="whitespace-nowrap">
                                  <Figured parts={splitLineTail(line.parts).tail} />
                                  <span className={cn(PILL_BASE, STATE_PILL[line.pill.tone], 'ml-1.5')}>{line.pill.word}</span>
                                </span>
                              </>
                            ) : (
                              <Figured parts={line.parts} />
                            )}
                          </span>
                        ))
                      : <span className="text-muted">—</span>}
                  </span>
                  <span className="flex items-center justify-end gap-1.5 whitespace-nowrap text-[11px] text-muted">
                    {row.inBandLine ? <span>{row.inBandLine}</span> : null}
                    {row.notComputedLine ? <span>{row.notComputedLine}</span> : null}
                    <StageStatus row={row} />
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Commentable>
      ) : null}
      {samples.count ? (
        <Commentable name="SamplePoints">
          <div data-stage-samples>
            <SubHead label="Sample points" meta={samplesMeta} />
            <div>
              {samples.rows.map((row) => (
                <div
                  key={row.id}
                  data-stage-sample={row.id}
                  className="grid grid-cols-[minmax(0,15rem)_minmax(0,1fr)_auto] items-baseline gap-x-3 border-b border-[var(--color-divider)] py-2 text-[13px] last:border-b-0"
                >
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span className="font-mono text-xs font-semibold text-ink-primary">{row.id}</span>
                    <span className="min-w-0 text-xs text-muted">{row.stream}</span>
                  </span>
                  <span className="min-w-0 text-ink-secondary">
                    {row.spec} <span className="text-xs text-muted">· {row.latency}</span>
                  </span>
                  <span className="flex justify-end">
                    {row.tone ? (
                      <span className={cn(PILL_BASE, STATE_PILL[row.tone])}>{row.adherence}</span>
                    ) : (
                      <span className="text-[11px] text-muted">{row.adherence}</span>
                    )}
                  </span>
                </div>
              ))}
              {samples.routine.length ? (
                <div
                  data-stage-sample-routine
                  className="grid grid-cols-[minmax(0,15rem)_minmax(0,1fr)_auto] items-baseline gap-x-3 py-2 text-[13px]"
                >
                  <span className="flex flex-wrap gap-x-2 font-mono text-xs font-semibold text-ink-primary">
                    {samples.routine.map((row) => (
                      <span key={row.id} title={`${row.stream} · ${row.spec}`}>
                        {row.id}
                      </span>
                    ))}
                  </span>
                  <span className="min-w-0 text-xs text-muted">
                    {samples.routine.map((row) => row.stream).join(' · ')}
                  </span>
                  <span className="text-[11px] text-muted">REG</span>
                </div>
              ) : null}
            </div>
          </div>
        </Commentable>
      ) : null}
    </SurfacePagePanel>
  );
}
